import { useAtom } from 'jotai';
import { groupBy, sortBy, startCase } from 'lodash';
import { useMemo } from 'react';

import {
  getComplianceFilterOptions,
  SEARCHABLE_SELECT_FILTERS_ASSET
} from '@/features/AssetSearch';
import SearchBar, { getSearchableSelectOptions } from '@/features/SearchBar';
import {
  assetsFiltersAtom,
  assetsTimeFilterAtom,
  filteredAssetsAtom
} from '@/pages/Assets';
import { toTitleCase } from '@/utils/Common.utils';

// TODO: last_seen_at time seems to be mapped to First Seen button, also last_seen_at filtering doesn't work for manually added assets, which do not have a last_seen_at field
const AssetSearch = () => {
  const [assetsFilters, setAssetsFilters] = useAtom(assetsFiltersAtom);
  const [filteredAssets] = useAtom(filteredAssetsAtom);
  const [, setAssetsTimeFilter] = useAtom(assetsTimeFilterAtom);

  const assetsSearchableFilters = useMemo(() => {
    const searchableSelectFilters = SEARCHABLE_SELECT_FILTERS_ASSET.map(
      (filter) => {
        let filterOptions = [];
        const getFilterField = (field: string) => {
          switch (field) {
            case 'risk_score':
              return 'score_severity';
            default:
              return field;
          }
        };

        if (filter.field === 'compliance_tags') {
          filterOptions = getComplianceFilterOptions(filteredAssets);
        } else if (filter.field === 'data_provider') {
          const options: string[] = [];
          filteredAssets.forEach((asset) => {
            options.push(...(asset.data_provider ?? []));
          });
          const groupedOptions = groupBy(options);

          filterOptions = Object.keys(groupedOptions).map((key) => ({
            label: key === 'vmpe' ? key.toUpperCase() : startCase(key),
            value: key,
            count: groupedOptions[key].length,
            id: key
          }));
        } else if (filter.field === 'operating_system') {
          const searchableSelectOptions = getSearchableSelectOptions(
            filteredAssets,
            getFilterField(filter.field),
            filter.title
          );

          // Deduplicate operating systems by normalizing case differences
          const dedupeMap = new Map();
          searchableSelectOptions.forEach((option) => {
            // Handle "No Operating System" case
            if (option.value === '' || option.label.startsWith('No ')) {
              const key = 'no_os';
              if (dedupeMap.has(key)) {
                const existing = dedupeMap.get(key);
                existing.count += option.count || 0;
              } else {
                dedupeMap.set(key, {
                  label: `No ${filter.title}`,
                  value: '',
                  count: option.count || 0
                });
              }
              return;
            }

            // Handle regular operating systems with case-insensitive deduplication
            // Use lowercase for the key to deduplicate, but preserve original value's punctuation for the label
            const normalizedKey = String(option.value).toLowerCase();
            if (dedupeMap.has(normalizedKey)) {
              const existing = dedupeMap.get(normalizedKey);
              existing.count += option.count || 0;
            } else {
              dedupeMap.set(normalizedKey, {
                label: toTitleCase(option.value as string),
                value: option.value, // Keep original value for filtering
                count: option.count || 0
              });
            }
          });

          // Sort with "No Operating System" first, then alphabetically
          filterOptions = sortBy(Array.from(dedupeMap.values()), (item) =>
            item.value === '' ? '' : item.label.toLowerCase()
          );
        } else {
          filterOptions = getSearchableSelectOptions(
            filteredAssets,
            getFilterField(filter.field),
            filter.title
          );
        }

        return {
          ...filter,
          config: {
            ...filter.config,
            variant: filter.config.variant
          },
          filterOptions: [...filter.filterOptions, ...filterOptions]
        };
      }
    );

    return searchableSelectFilters;
  }, [filteredAssets]);

  return (
    <SearchBar
      className="px-8 pb-4"
      createdTimeLabel="First Seen"
      lastUpdatedTimeLabel="Last Seen"
      enableTimeFilter
      searchableSelectFilters={assetsSearchableFilters}
      selectedFilters={assetsFilters}
      onFilterChange={setAssetsFilters}
      onTimeFilterChange={setAssetsTimeFilter}
    />
  );
};

export default AssetSearch;
